import clsx from 'clsx'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { CheckStamp } from '@/src/elements/checkstamp/Checkstamp'
import { Typography } from '@/src/elements/text/Typography'
import { COMMUNITY_LIFETIME } from '@/src/features/community/models/community.constants'
import { formatCalendar } from '@/src/general/primitives/dates/dayjs'
import { capitalizeFirstLetter } from '@/src/general/primitives/strings/capitalize'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TPublicationApi } from '../../../api/types/getPublication.types'
import type { TPickPublication } from '../../../models/publication.types'
import { removeDayPrefix } from '../../../utils/removeDayPrefix'
import { PublicationContentLabel } from './PublicationContentLabel'

export type TPublicationDetailsProps = {
  publication: TPickPublication<
    TPublicationApi,
    'content' | 'effectivePublicationDate' | 'isScheduled' | 'name' | 'published' | 'rank' | 'community.lifetime'
  >
  className?: string
  variant: 'default' | 'primary' | 'success'
  noDayPrefix?: boolean
}

export const PublicationDetails = ({
  publication,
  className,
  variant,
  noDayPrefix,
}: TPublicationDetailsProps): ReactNode => {
  const { t } = useTranslate()

  const {
    name: publicationTitle,
    effectivePublicationDate,
    community: { lifetime },
    rank,
    isScheduled,
    published,
  } = publication

  const textColorClassName = isScheduled && !published ? 'text-[#3F2B18] opacity-50' : undefined

  return (
    <View className={clsx('flex-col items-start gap-2 px-2 py-1', className)}>
      <View className="flex-row items-center justify-between w-full gap-6">
        <View className="flex-row items-center gap-1.5">
          {variant === 'success' && <CheckStamp state="valid" size="sm" checkAnimationDelay={0} />}
          <Typography
            colors={variant === 'success' ? 'success' : 'primary'}
            contrast="low"
            size="xs"
            className={textColorClassName}
          >
            {lifetime === COMMUNITY_LIFETIME.EVERLASTING
              ? capitalizeFirstLetter(formatCalendar(effectivePublicationDate, t))
              : typeof rank === 'number' && <FormattedMessage id="day.count" values={{ number: rank }} />}
          </Typography>
        </View>
        <PublicationContentLabel content={publication.content} colors={variant} className={textColorClassName} />
      </View>
      <Typography
        bold
        colors={variant}
        contrast="high"
        size="md"
        numberOfLines={2}
        className={clsx('overflow-ellipsis', textColorClassName)}
      >
        {noDayPrefix ? removeDayPrefix(publicationTitle) : publicationTitle}
      </Typography>
    </View>
  )
}
